import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, useFormState } from 'react-final-form';
import { useForm } from 'react-final-form';
import { ThreeDots } from 'react-loader-spinner';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';

import CommonButton from '../../components/common-button/CommonButton';
import passwordGenerator from '../../utils/passwordGeneration';
import WithLabel, { LABEL_STYLES } from '../../components/with-label/WithLabel';
import WithError from '../../components/with-error/WithError';
import { Input } from '../../components/common-inputs';
import PasswordInput from '../../components/common-inputs/PasswordInput';
import CommonDropdown from '../../components/common-dropdown/CommonDropdown';
import useUserRolesDropdown from '../../hooks/useUserRolesDropdown';

import scss from './UserForm.module.scss';

const UserForm = ({ handleSubmit, isCreate = true }) => {
  const { t } = useTranslation('userManagement');
  const navigate = useNavigate();
  const isLoading = useSelector((state) => state.userManagement.isLoading);
  const { valid } = useFormState();
  const isSubmitDisabled = !valid || isLoading;
  const form = useForm();
  const userRoles = useUserRolesDropdown();

  const handleGeneratePassword = useCallback(() => {
    const newPassword = passwordGenerator();
    form.change('password', newPassword);
    form.change('repeatPassword', newPassword);
  }, [form]);

  const handleRestForm = useCallback(() => {
    form.restart();
    navigate('/users');
  }, [form, navigate]);

  return (
    <form className={scss.form} onSubmit={handleSubmit}>
      <Field name='name' autoComplete='name'>
        {({ meta, input }) => (
          <WithLabel variant={LABEL_STYLES.ALIGNED} label={t('name')} extraClassName={scss.inputLabel}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <Input
                type='text'
                size='sm'
                placeholder={t('name')}
                tabIndex={1}
                isError={meta.touched && meta.error}
                formProps={input}
                disabled={isLoading}
                autoComplete='name'
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <Field name='email' autoComplete='email'>
        {({ meta, input }) => (
          <WithLabel variant={LABEL_STYLES.ALIGNED} label={t('email')} extraClassName={scss.inputLabel}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <Input
                type='text'
                size='sm'
                placeholder={t('email')}
                tabIndex={2}
                isError={meta.touched && meta.error}
                formProps={input}
                disabled={isLoading}
                autoComplete='email'
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      {isCreate ? (
        <>
          <Field name='password' autoComplete='new-password'>
            {({ meta, input }) => (
              <div className={scss.inputWithButton}>
                <WithLabel variant={LABEL_STYLES.ALIGNED} label={t('password')} extraClassName={scss.inputLabel}>
                  <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                    <PasswordInput
                      size='sm'
                      placeholder={t('password')}
                      tabIndex={3}
                      isError={meta.touched && meta.error}
                      formProps={input}
                      disabled={isLoading}
                      autoComplete='new-password'
                    />
                  </WithError>
                </WithLabel>

                <CommonButton
                  type='button'
                  styleColor='primary'
                  tabIndex={4}
                  onClick={handleGeneratePassword}
                  disabled={isLoading}
                  maxWidth={100}>
                  {t('generate')}
                </CommonButton>
              </div>
            )}
          </Field>

          <Field name='repeatPassword' autoComplete='new-password'>
            {({ meta, input }) => (
              <WithLabel variant={LABEL_STYLES.ALIGNED} label={t('reenterPassword')} extraClassName={scss.inputLabel}>
                <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                  <PasswordInput
                    size='sm'
                    placeholder={t('reenterPassword')}
                    tabIndex={5}
                    isError={meta.touched && meta.error}
                    formProps={input}
                    disabled={isLoading}
                    autoComplete='new-password'
                  />
                </WithError>
              </WithLabel>
            )}
          </Field>
        </>
      ) : null}

      <Field name='roleName'>
        {({ meta, input }) => (
          <WithLabel variant={LABEL_STYLES.ALIGNED} label={t('userType')} extraClassName={scss.inputLabel}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <CommonDropdown
                options={userRoles}
                value={input.value}
                onSelect={input.onChange}
                disabled={isLoading}
                maxWidth={180}
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <div className={scss.actions}>
        <CommonButton
          type='submit'
          styleColor='primary'
          onClick={handleSubmit}
          maxWidth={100}
          tabIndex={6}
          disabled={isSubmitDisabled}>
          {isLoading ? <ThreeDots height={15} width={40} /> : null}
          {isCreate ? t('create') : t('edit')}
        </CommonButton>
        <CommonButton
          type='button'
          styleColor='secondary'
          onClick={handleRestForm}
          maxWidth={100}
          disabled={isLoading}
          tabIndex={7}>
          {t('cancel')}
        </CommonButton>
      </div>
    </form>
  );
};

export default UserForm;
